import { CrashRule } from '@enums/crash-rule'
import { errorStack } from '@utils/crash-report'

/**
 * Globals that browsers and extensions inject into every page, never defined or read by our code: Firefox for iOS's
 * helpers, crypto wallets' provider and the Dark Reader extension. An error must name one of them to be set aside.
 */
const INJECTED_GLOBALS = /\b(?:__firefox__|ethereum|DarkReader)\b/
const SCRIPT_FILE = /\.m?js$/
// The only frame WebKit gives to code evaluated in the document itself: `global code@<document URL>:1:<column>`.
const DOCUMENT_FRAME = 'global code@'
const DIGITS = /^\d+$/

// `new URL` throws on what browsers hand when they have no file to name (`''`, `'undefined'`): that is not a document.
const parsedUrl = (filename: string): URL | undefined => {
	try {
		return new URL(filename)
	} catch {
		return undefined
	}
}

/**
 * Whether the error is located in the page document rather than in one of our files. The page has no inline script
 * (CI checks `src/index.html`) and every file we ship ends in `.js`, so only injected code runs there.
 */
const isInDocument = (filename: string, origin: string): boolean => {
	const url = parsedUrl(filename)

	return url?.origin === origin && !SCRIPT_FILE.test(url.pathname)
}

/** Whether the stack is a single frame on the first line of that document, as WebKit writes injected code. */
const isSingleDocumentFrame = (error: unknown, filename: string): boolean => {
	const stack = errorStack(error)

	if (typeof stack !== 'string') return false

	const frames = stack.trim().split('\n')
	const prefix = `${DOCUMENT_FRAME}${filename}:1:`

	return (
		frames.length === 1 &&
		frames[0].startsWith(prefix) &&
		DIGITS.test(frames[0].slice(prefix.length))
	)
}

/**
 * The rule that proves a window error is not ours, or `undefined` to keep it a crash. A rule needs positive evidence
 * of where the error came from, so anything unknown, partial or in another browser's format stays a crash. Errors
 * Angular catches and unhandled rejections are never classified.
 *
 * `injected-code`: the error names an injected global, sits on line 1 of the page document and its stack is that one
 * frame. All 39 groups of this kind in production (2026-10) had exactly this shape.
 */
export const classifyWindowError = (
	{ message, filename, lineno, error }: ErrorEvent,
	origin: string
): CrashRule | undefined => {
	if (!INJECTED_GLOBALS.test(message)) return undefined
	if (lineno !== 1 || !isInDocument(filename, origin)) return undefined
	if (!isSingleDocumentFrame(error, filename)) return undefined

	return CrashRule.InjectedCode
}
